import i18next from "i18next";
import Cookies from "js-cookie";
import React, { useContext, useEffect, useState, useRef } from "react";
import { initReactI18next } from "react-i18next";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
  useParams
} from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { io } from "socket.io-client";
import {
  selectIsConnectedToRoom,
  selectLocalPeer,
  useHMSActions,
  useHMSStore
} from "@100mslive/react-sdk";

import { WorkshopContext } from "./contexts/workshop.context";
import {
  fetchTranslations,
  getBoosterWorkshopDetails,
  getPortalDetails,
  getWorkshopInsightsData
} from "./service";

import BugReport from "./components/bugReport";
import { Loader } from "./components/loaders";
import {
  BoosterBenefits,
  BoosterLogin,
  BoosterStrengths,
  BoosterThankYou,
  BoosterUnderstanding,
  BoosterWait,
  BoosterWordCloud,
  End,
  Insights,
  Login,
  PreSummary,
  Summary,
  Task1,
  Task2,
  Task3,
  Task4,
  Task5,
  Task6,
  Task7,
  Task8,
  Task8b,
  Task8c,
  Task9,
  Task9b,
  Task10,
  Task10b,
  Wait,
  OnlineLogin,
  OnlineWaitingRoom,
  OnlineTask1,
  OnlineTask2,
  OnlineTask3,
  OnlineTask4,
  OnlineVideoPage,
  OnlineTask5,
  OnlineTask6,
  OnlineTask7,
  OnlineTask8a,
  OnlineTask8b,
  OnlineTask8c,
  OnlineTask9,
  OnlineTask9b,
  OnlineTask10,
  OnlinePreSummary,
  OnlinePreReady,
  OnlineInsightsViewer,
  OnlineDemoSetup,
  OnlineEndCall
} from "./pages";

import { NewUserNotification } from "./components/workshop";
import VideoPage from "./pages/videoPage";
import { hexToRgba } from "./util/colors.util";
import { defaultPlatforms, defaultRoles, NonPrivateRoutes } from "./util/workshop.util";
import {
  getLocalStorage,
  demoWorkshopUsers,
  clearLocalStorage,
  setLocalStorage
} from "./util/general.util";

import { demoUsers } from "./util/demoData.util";

const VALID_LANGUAGES = ["en", "de", "fr", "it"];

const App = () => {
  const { workshopDetails } = useContext(WorkshopContext);

  const isRemote = useLocation().pathname.includes("online");

  return (
    <div
      id="base-container"
      className={`font-inter ${!workshopDetails?.pdf && !isRemote ? "container mx-auto p-6 py-12 min-h-dvh" : ""}`}>
      <ToastContainer
        hideProgressBar={true}
        newestOnTop={true}
        pauseOnFocusLoss={false}
        pauseOnHover={false}
        limit={1}
      />
      <Routes>
        <Route path="/" element={<ScrollTopContainer />}>
          <Route path="end-call" element={<OnlineEndCall />} />

          <Route path="" element={<BaseContainer />}>
            <Route path="login" element={<Login />} />
            <Route path="wait" element={<Wait />} />
            <Route path="video/:videoId" element={<VideoPage />} />
            <Route path="task-1" element={<Task1 />} />
            <Route path="task-2" element={<Task2 />} />
            <Route path="task-3" element={<Task3 />} />
            <Route path="task-4" element={<Task4 />} />
            <Route path="task-5" element={<Task5 />} />
            <Route path="task-6" element={<Task6 />} />
            <Route path="task-7" element={<Task7 />} />
            {/* 
          Task 8 done like this now because with useParams, 
          it still picked up type from previous page in first render
           and it was cuasing issues
          */}
            <Route path="task-8/a" element={<Task8 type={"a"} />} />
            <Route path="task-8/b" element={<Task8b type={"b"} />} />
            <Route path="task-8/c" element={<Task8c type={"c"} />} />
            <Route path="task-9" element={<Task9 />} />
            <Route path="task-9/b" element={<Task9b />} />
            <Route path="task-10" element={<Task10 />} />
            <Route path="task-10/b" element={<Task10b />} />
            <Route path="pre-summary" element={<PreSummary />} />
            <Route path="summary" element={<Summary />} />
            <Route path="end" element={<End />} />
          </Route>

          <Route path="online" element={<OnlineWorkshopContainer />}>
            <Route path="" element={<OnlineLogin />} />
            <Route path="pre-ready" element={<OnlinePreReady />} />
            <Route path="wait" element={<OnlineWaitingRoom />} />
            <Route path="video/:videoId" element={<OnlineVideoPage />} />
            <Route path="task-1" element={<OnlineTask1 />} />
            <Route path="task-2" element={<OnlineTask2 />} />
            <Route path="task-3" element={<OnlineTask3 />} />
            <Route path="task-4" element={<OnlineTask4 />} />
            <Route path="task-5" element={<OnlineTask5 />} />
            <Route path="task-6" element={<OnlineTask6 />} />
            <Route path="task-7" element={<OnlineTask7 />} />
            <Route path="task-8/a" element={<OnlineTask8a type={"a"} />} />
            <Route path="task-8/b" element={<OnlineTask8b type={"b"} />} />
            <Route path="task-8/c" element={<OnlineTask8c type={"c"} />} />
            <Route path="task-9" element={<OnlineTask9 />} />
            <Route path="task-9/b" element={<OnlineTask9b />} />
            <Route path="task-10" element={<OnlineTask10 />} />
            <Route path="pre-summary" element={<OnlinePreSummary />} />
            <Route path="insights/:id" element={<OnlineInsightsViewer />} />
          </Route>

          <Route path="online-demo" element={<OnlineDemoContainer />}>
            <Route path="" element={<OnlineDemoSetup />} />
            <Route path="wait" element={<OnlineWaitingRoom demoWorkshop={true} />} />
            <Route path="video/:videoId" element={<OnlineVideoPage demoWorkshop={true} />} />
            <Route path="task-6" element={<OnlineTask6 demoWorkshop={true} />} />
            <Route path="task-8/a" element={<OnlineTask8a type={"a"} demoWorkshop={true} />} />
            <Route path="task-8/b" element={<OnlineTask8b type={"b"} demoWorkshop={true} />} />
            <Route path="task-8/c" element={<OnlineTask8c type={"c"} demoWorkshop={true} />} />
            <Route path="pre-summary" element={<OnlinePreSummary demoWorkshop={true} />} />
            <Route path="insights/:id" element={<OnlineInsightsViewer demoWorkshop={true} />} />

            <Route path="*" element={<Navigate replace to="/online-demo" />} />
          </Route>

          <Route path="booster" element={<BoosterContainer />}>
            <Route path="" element={<BoosterLogin />} />
            <Route path="wait" element={<BoosterWait />} />
            <Route path="understanding" element={<BoosterUnderstanding />} />
            <Route path="choose-strengths" element={<BoosterStrengths />} />
            <Route path="world-cloud" element={<BoosterWordCloud />} />
            <Route path="benefits" element={<BoosterBenefits />} />
            <Route path="thank-you" element={<BoosterThankYou />} />
          </Route>

          <Route path="insights" element={<InsightsContainer />}>
            <Route path=":id" element={<Insights />} />
          </Route>

          <Route path="/demo" element={<DemoContainer />}>
            <Route path="" element={<Navigate to="/demo/wait" />} />
            <Route path="wait" element={<Wait demoWorkshop={true} />} />
            <Route path="video/:videoId" element={<VideoPage demoWorkshop={true} />} />
            <Route path="task-1" element={<Task6 demoWorkshop={true} />} />
            <Route path="task-2/a" element={<Task8 type={"a"} demoWorkshop={true} />} />
            <Route path="task-2/b" element={<Task8b type={"b"} demoWorkshop={true} />} />
            <Route path="task-2/c" element={<Task8c type={"c"} demoWorkshop={true} />} />
            <Route path="end" element={<PreSummary demoWorkshop={true} />} />
            <Route path="*" element={<Navigate replace to="/demo" />} />
          </Route>

          {/* Not found */}
          <Route path="not-found" element={<NotFound />} />
          <Route path="*" element={<Navigate replace to="/not-found" />} />
        </Route>
      </Routes>

      {process.env.NODE_ENV === "production" && <BugReport />}
    </div>
  );
};

const DemoContainer = () => {
  const { setSocket, setThemeConfig, setUser, setWorkshopDetails, setPlatform, setWorkshopUsers } =
    useContext(WorkshopContext);

  const [load, setLoad] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const loadTranslations = async () => {
    try {
      // Fetch translations in parallel
      const [workshopTranslations, resourceTranslations] = await Promise.all([
        fetchTranslations("demo-mobile-workshop"),
        fetchTranslations("workshop-resources")
      ]);

      // Create resources object with fallback to English if a language is missing
      const resources = {};
      VALID_LANGUAGES.forEach((lang) => {
        const workshopLangData =
          workshopTranslations.data?.[lang] || workshopTranslations.data?.en || {};
        const resourceLangData =
          resourceTranslations.data?.[lang] || resourceTranslations.data?.en || {};

        resources[lang] = {
          translations: {
            ...workshopLangData,
            resources: resourceLangData
          }
        };
      });

      // Get and validate language parameter
      const langParam = searchParams.get("lang")?.toLowerCase();
      const selectedLanguage = VALID_LANGUAGES.includes(langParam) ? langParam : "en";

      await i18next.use(initReactI18next).init({
        interpolation: { escapeValue: false },
        saveMissing: false,
        returnEmptyString: false,
        fallbackLng: "en",
        lng: selectedLanguage,
        languages: VALID_LANGUAGES,
        ns: ["translations"],
        defaultNS: "translations",
        resources
      });
    } catch (error) {
      console.error("Failed to load translations:", error);
      await i18next.use(initReactI18next).init({
        lng: "en",
        fallbackLng: "en"
      });
    }
  };

  const setupWorkshop = async () => {
    const workshopUsers = [];
    let demoUser = getLocalStorage("demoUser");

    // Create URLSearchParams object for query parameters
    const queryParams = new URLSearchParams();

    // Add parameters if they exist
    const langParam = searchParams.get("lang");
    const nameParam = searchParams.get("name") || "Demo User";

    if (langParam) queryParams.set("lang", langParam);
    if (nameParam) queryParams.set("name", nameParam);

    // Convert to query string (includes ? prefix if params exist)
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

    // Check if demoUser has all required properties
    const isValidDemoUser =
      demoUser && "id" in demoUser && "name" in demoUser && "role" in demoUser;

    // Create new demo user if invalid, missing properties, or name is different
    if (nameParam && (!isValidDemoUser || demoUser.name !== nameParam)) {
      demoUser = {
        id: "demo",
        name: nameParam,
        role: defaultRoles.moderatorAndParticipant
      };
      setLocalStorage("demoUser", demoUser);
    }

    if (demoUser) workshopUsers.push(demoUser);
    else if (!["/demo", "/demo/wait"].includes(window.location.pathname)) {
      navigate(`/demo${queryString}`);
      clearLocalStorage();
    }

    workshopUsers.push(...demoUsers);
    await loadTranslations();
    setPlatform(defaultPlatforms.mtDemo);
    setUser(demoUser || {});
    setWorkshopDetails({ platform: defaultPlatforms.mtDemo, teamName: "Team Explorers" });
    setWorkshopUsers(workshopUsers);
    setThemeConfig({ primary: "#E59536", primary50: hexToRgba("#E59536", 50) });
    setSocket(null);

    setLoad(true);
  };

  useEffect(() => {
    setupWorkshop();
  }, []);

  useEffect(() => {
    if (load) {
      if (window.location.pathname === "/demo" || window.location.pathname === "/demo/") {
        clearLocalStorage();
        setupWorkshop();
      }
    }
  }, [window.location.pathname, load]);

  if (!load) return <Loader message="Setting up the workshop" />;

  return (
    <div
      className={`${!["/demo/task-2/a", "/demo/task-2/b"].includes(window.location.pathname) ? "max-w-[1000px] mx-auto pb-48" : ""}`}>
      <Outlet />
    </div>
  );
};

const ScrollTopContainer = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return <Outlet />;
};

const BaseContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  let m = searchParams.get("m"); // Moderator token
  let p = searchParams.get("p"); // Participant token
  let mp = searchParams.get("mp"); // NoderatorParticipant token
  let mtWorkshop = searchParams.get("mt"); // Mission Team Workshop token
  const pdf = searchParams.get("pdf"); // PDF token
  const pdfSection = searchParams.get("pdfSection"); // pdfSection token

  const [locked, setLocked] = useState(false);

  try {
    // Remove previous user ID as it is a new login to with workshop it
    if (m || p || mp) Cookies.remove("userId");
    if (m) {
      Cookies.remove("p");
      Cookies.remove("mp");
    }
    if (p) {
      Cookies.remove("m");
      Cookies.remove("mp");
    }
    if (mp) {
      Cookies.remove("m");
      Cookies.remove("p");
    }

    // remove cookies if not on summary page
    if (!location.pathname.includes("/summary") && (p || m || mp)) {
      Cookies.remove("consent");
    }
  } catch (error) {
    console.log("Cookies Unavailable");
  }

  if (!m && !p && !mp) {
    try {
      m = Cookies.get("m"); // Try to get m token from cookies
      if (!m) p = Cookies.get("p"); // If m is not present, then try for p token only
      if (!m && !p) mp = Cookies.get("mp"); // If m and p are not present, then try for mp token only
    } catch (error) {
      console.log("Cookies Unavailable");
    }
  }

  const {
    socket,
    setSocket,
    setThemeConfig,
    user,
    setUser,
    setWorkshopDetails,
    setPlatform,
    setWorkshopUsers,
    setAllUsersWithMods
  } = useContext(WorkshopContext);

  const [load, setLoad] = useState(false);

  const loadTranslations = async (type) => {
    const translationId = {
      mt: "physical-mission-team-workshop",
      mft: "physical-mission-future-workshop",
      mtOnline: "online-mission-team-workshop"
    };

    const { data: { en, de, fr, it } = {} } = await fetchTranslations(translationId[type]);
    const { data: { en: en_, de: de_, fr: fr_, it: it_ } = {} } =
      await fetchTranslations("workshop-resources");

    i18next.addResourceBundle("en", "translations", { ...en, resources: { ...en_ } });
    i18next.addResourceBundle("fr", "translations", { ...fr, resources: { ...fr_ } });
    i18next.addResourceBundle("de", "translations", { ...de, resources: { ...de_ } });
    i18next.addResourceBundle("it", "translations", { ...it, resources: { ...it_ } });

    i18next.changeLanguage("en"); // Initial language
  };

  useEffect(() => {
    i18next.init({
      interpolation: { escapeValue: false },
      saveMissing: false,
      returnEmptyString: false
    });
    i18next.languages = ["en", "fr", "de", "it"];
    i18next.use(initReactI18next).init({
      fallbackLng: "en",
      lng: "en",
      ns: ["translations"],
      defaultNS: "translations"
    });

    (async () => {
      try {
        const { data: { workshop, theme = {} } = {}, mtW } = await getPortalDetails(m, p, mp);

        const platform =
          mtWorkshop === "true" || mtW
            ? workshop?.type === "online"
              ? "mtOnline"
              : "mt"
            : workshop?.platform || "mft";

        await loadTranslations(platform);
        setUser({ m, p, mp });

        setPlatform(platform);
        setThemeConfig({ ...theme, primary: "#E59536", primary50: hexToRgba("#E59536", 50) });
        setWorkshopDetails({
          ...workshop,
          pdf,
          pdfSection,
          platform
        });

        i18next.changeLanguage(workshop?.languageId);

        try {
          document.documentElement.setAttribute("lang", workshop?.languageId);
        } catch (error) {
          console.log("HTML lang attribute not found");
        }

        try {
          // Store in cookies that expire in 3 hours
          if (m) Cookies.set("m", m, { expires: 0.125 });
          if (p) Cookies.set("p", p, { expires: 0.125 });
          if (mp) Cookies.set("mp", mp, { expires: 0.125 });
        } catch (error) {
          console.log("Cookies Unavailable");
        }

        // Connect to socket
        const socketConnection = io(process.env.REACT_APP_SOCKET_URL + "/workshop", {
          extraHeaders: {
            workshopId: workshop?._id,
            platform: platform
          }
        });

        socketConnection.on("connect", () => {
          console.log("Mobile Workshop Socket connected");
          setSocket(socketConnection);

          socketConnection.on("users", (users) => {
            setWorkshopUsers(users);
          });

          socketConnection.on("allUsersWithMods", (users) => {
            setAllUsersWithMods(users);
          });

          socketConnection.on("groupsRefreshed", (name) => {
            toast.info(name + " is getting ready to join the workshop.", {
              hideProgressBar: false,
              pauseOnHover: false,
              closeOnClick: true,
              autoClose: 2000,
              onClose: () => window.location.reload()
            });
          });

          socketConnection.on("workshopEnded", (status) => {
            if (status) {
              console.log("workshopEndedCAUGHT", status,workshop);
              if (workshop?.type !== "online") navigate("/pre-summary");
              // window.location.href = `/insights/${workshop?._id}`;
              // toast.info("This works is ended");
            }
          });

          socketConnection.on("disconnect", () => {
            console.log("disconnect");
            setSocket(null);
            setUser({ m, p, mp });
            navigate("/login");
            toast.info(
              "You are disconnected from server. We are trying to connect you again. Please hold on.",
              {
                autoClose: true,
                hideProgressBar: false
              }
            );
          });

          socketConnection.on("error", (error) => {
            toast.error(error);
            console.log(error);
          });
        });
      } catch (error) {
        console.log(error);
        toast.error(error?.response?.data || "Portal not found");
        navigate(`/not-found`);
      }
    })();
  }, []);

  // Load the workshop once socket is connected
  useEffect(() => {
    if (socket?.connected) {
      setLoad(true);

      if (pdf) return;

      try {
        // Check if userId exists in cookies, if yes, then get user details from socket and set it in context
        const userId = Cookies.get("userId");
        if (userId) {
          socket.emit("getUserData", userId, (user) => {
            if (user) {
              setUser({ ...user, m, p, mp });
              toast.success(user?.name + ", You are connected again.");

              socket.emit("syncToWorkshop", "", (url) => {
                const redirectUrl = url || "/wait";
                navigate(redirectUrl);
              });
            } else {
              Cookies.remove("userId");
              navigate("/login");
            }
          });
        } else {
          if (window.location.pathname !== "/login" && !p && !m && !mp) navigate("/not-found");
          else navigate("/login");
        }
      } catch (error) {
        navigate("/not-found");
        console.log(error);
        console.log("Cookies Unavailable");
      }
    }
  }, [socket]);

  useEffect(() => {
    if (socket?.connected && user?.id) {
      // Check if page is unlocked
      if (NonPrivateRoutes.includes(location.pathname)) {
        setLocked(false);
        return;
      }
      socket.emit("lockedPage", location.pathname, (unlocked) => {
        if (pdf || location.pathname?.includes("pre-summary")) {
          setLocked(false);
          return;
        }

        setLocked(!unlocked);
        if (!unlocked) toast.error("Locked");
      });
    }
  }, [location.pathname, user, socket]);

  useEffect(() => {
    if (!user?.id || !socket) return;

    socket.on("navigate", (path) => {
      if (!user?.id) return;
      navigate(path);
    });
  }, [socket, user]);

  if (!load || !socket) return <Loader message="Setting up the workshop" />;

  if (locked) return <LockedPage />;
  else
    return (
      <>
        <div
          className={`${!["/summary", "/task-8/a", "/task-8/b"].includes(location.pathname) ? "max-w-[1000px] mx-auto pb-48" : ""}`}>
          <Outlet />

          {/* {(user?.role === defaultRoles.moderator ||
            user?.role === defaultRoles.moderatorAndParticipant) &&
            !NonPrivateRoutes.includes(location.pathname) && (
              <QRPopup teamName={workshopDetails?.teamName} />
            )} */}
        </div>
        {(user?.role === defaultRoles.moderator ||
          user?.role === defaultRoles.moderatorAndParticipant) && <NewUserNotification />}
      </>
    );
};

const OnlineWorkshopContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hmsActions = useHMSActions();
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const localPeer = useHMSStore(selectLocalPeer);

  // Use refs to track current values for use in event handlers
  const isConnectedRef = useRef(isConnected);
  const localPeerRef = useRef(localPeer);

  // Update refs when values change
  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  useEffect(() => {
    localPeerRef.current = localPeer;
  }, [localPeer]);

  const [searchParams] = useSearchParams();
  let m = searchParams.get("m"); // Moderator token
  let p = searchParams.get("p"); // Participant token
  let mp = searchParams.get("mp"); // NoderatorParticipant token
  const pdf = searchParams.get("pdf"); // PDF token
  const pdfSection = searchParams.get("pdfSection"); // pdfSection token

  const [locked, setLocked] = useState(false);

  try {
    // Remove previous user ID as it is a new login to with workshop it
    if (m || p || mp) {
      Cookies.remove("userId");
      Cookies.remove("setupCompleted");
    }
    if (m) {
      Cookies.remove("p");
      Cookies.remove("mp");
    }
    if (p) {
      Cookies.remove("m");
      Cookies.remove("mp");
    }
    if (mp) {
      Cookies.remove("m");
      Cookies.remove("p");
    }

    // remove cookies if not on summary page
    if (!location.pathname.includes("/summary") && (p || m || mp)) {
      Cookies.remove("consent");
    }
  } catch (error) {
    console.log("Cookies Unavailable");
  }

  if (!m && !p && !mp) {
    try {
      m = Cookies.get("m"); // Try to get m token from cookies
      if (!m) p = Cookies.get("p"); // If m is not present, then try for p token only
      if (!m && !p) mp = Cookies.get("mp"); // If m and p are not present, then try for mp token only
    } catch (error) {
      console.log("Cookies Unavailable");
    }
  }

  const {
    socket,
    setSocket,
    setThemeConfig,
    user,
    setUser,
    setWorkshopDetails,
    workshopDetails,
    setPlatform,
    setWorkshopUsers,
    setAllUsersWithMods,
    setConnectedUsers,
    setDisconnectedUsers
  } = useContext(WorkshopContext);

  const [load, setLoad] = useState(false);

  const loadTranslations = async (type) => {
    const translationId = {
      mt: "physical-mission-team-workshop",
      mft: "physical-mission-future-workshop",
      mtOnline: "online-mission-team-workshop"
    };

    const { data: { en, de, fr, it } = {} } = await fetchTranslations(translationId[type]);
    const { data: { en: en_, de: de_, fr: fr_, it: it_ } = {} } =
      await fetchTranslations("workshop-resources");

    i18next.addResourceBundle("en", "translations", { ...en, resources: { ...en_ } });
    i18next.addResourceBundle("fr", "translations", { ...fr, resources: { ...fr_ } });
    i18next.addResourceBundle("de", "translations", { ...de, resources: { ...de_ } });
    i18next.addResourceBundle("it", "translations", { ...it, resources: { ...it_ } });

    i18next.changeLanguage("en"); // Initial language
  };

  useEffect(() => {
    i18next.init({
      interpolation: { escapeValue: false },
      saveMissing: false,
      returnEmptyString: false
    });
    i18next.languages = ["en", "fr", "de", "it"];
    i18next.use(initReactI18next).init({
      fallbackLng: "en",
      lng: "en",
      ns: ["translations"],
      defaultNS: "translations"
    });

    (async () => {
      try {
        const { data: { workshop, theme = {} } = {}, mtW } = await getPortalDetails(m, p, mp);

        const platform = "mtOnline";

        await loadTranslations(platform);
        setUser({ m, p, mp });
        setPlatform(platform);
        setThemeConfig({ ...theme, primary: "#E59536", primary50: hexToRgba("#E59536", 50) });
        setWorkshopDetails({ ...workshop, pdf, pdfSection, platform });

        i18next.changeLanguage(workshop?.languageId);

        try {
          document.documentElement.setAttribute("lang", workshop?.languageId);
        } catch (error) {
          console.log("HTML lang attribute not found");
        }

        try {
          // Store in cookies that expire in 3 hours
          if (m) Cookies.set("m", m, { expires: 0.125 });
          if (p) Cookies.set("p", p, { expires: 0.125 });
          if (mp) Cookies.set("mp", mp, { expires: 0.125 });
        } catch (error) {
          console.log("Cookies Unavailable");
        }

        // Connect to socket
        const socketConnection = io(process.env.REACT_APP_SOCKET_URL + "/workshop", {
          extraHeaders: {
            workshopId: workshop?._id,
            platform: platform
          }
        });

        socketConnection.on("connect", () => {
          console.log("Online Workshop Socket connected");
          setSocket(socketConnection);

          socketConnection.on("connectedUsers", (users) => {
            setConnectedUsers(users);
          });

          socketConnection.on("disconnectedUsers", (users) => {
            setDisconnectedUsers(users);
          });

          socketConnection.on("users", (users) => {
            setWorkshopUsers(users);
          });

          socketConnection.on("allUsersWithMods", (users) => {
            setAllUsersWithMods(users);
          });

          socketConnection.on("groupsRefreshed", (name) => {
            toast.info(name + " is getting ready to join the workshop.", {
              hideProgressBar: false,
              pauseOnHover: false,
              closeOnClick: true,
              autoClose: 3000,
              onClose: () => window.location.reload()
            });
          });

          socketConnection.on("workshopEnded", (status) => {
            console.log("workshopEnded", status);
            if (status) navigate("/online/pre-summary");
            setWorkshopDetails(prev => ({ ...prev, workshopEnded: status }));
            // if (status) window.location.href = `/insights/${workshop?._id}`;
          });
          socketConnection.on("getWorkshopStartedStatus", (status) => {
            setWorkshopDetails(prev => ({ ...prev, workshopStarted: status }));
            console.log("getWorkshopStartedStatus", status);
          });

          socketConnection.on("leaveWorkshop", ({ success }) => {
            if (success) {
              // Disconnect from 100ms call if connected
              // Use refs to get current values (avoid stale closure)
              if (isConnectedRef.current || localPeerRef.current) {
                try {
                  hmsActions.leave();
                } catch (error) {
                  console.error("Error leaving HMS call:", error);
                }
              }

              // Clear user data and cookies
              Cookies.remove("userId");
              Cookies.remove("setupCompleted");
              setUser({ m, p, mp });
              setSocket(null);

              // Navigate to end call page
              navigate("/online/end-call");
            }
          });

          socketConnection.on("disconnect", () => {
            setSocket(null);
            setUser({ m, p, mp });
            navigate("/online");
            toast.error(
              "You are disconnected from server, please use the login link again to join the workshop."
            );
          });

          socketConnection.on("error", (error) => {
            toast.error(error);
            console.log(error);
          });
        });
      } catch (error) {
        console.log(error);
        toast.error(error?.response?.data || "Portal not found");
        navigate(`/not-found`);
      }
    })();
  }, []);

  const joinCall = async () => {
    const authToken = await hmsActions.getAuthTokenByRoomCode({
      roomCode: workshopDetails?.callJoiningCode?.code
    });

    // Only start a preview here for first-time setup.
    // Rejoining users will go through an explicit interaction screen
    // and the actual join happens from the PreReady screen.
    hmsActions.preview({ userName: user?.name, authToken });
    console.log("started preview from joinCall in App", workshopDetails);
  };

  useEffect(() => {
    const setupCompleted = Cookies.get("setupCompleted") === "true";
    // Skip auto-preview for users who have already completed setup;
    // they will go through the rejoin gate + PreReady instead.
    if (
      !localPeer &&
      !isConnected &&
      workshopDetails?.callJoiningCode?.code &&
      user?.name &&
      !setupCompleted
    ) {
      joinCall();
    }
  }, [workshopDetails, user, hmsActions, localPeer, isConnected]);

  // Handle page unload/reload - disconnect 100ms call
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Disconnect if connected or in preview mode
      if (isConnected || localPeer) {
        try {
          hmsActions.leave();
        } catch (error) {
          console.error("Error leaving HMS call on unload:", error);
        }
      }
    };

    // Use beforeunload for better reliability
    window.addEventListener("beforeunload", handleBeforeUnload);
    // Also use pagehide as a fallback (works better on mobile)
    window.addEventListener("pagehide", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handleBeforeUnload);
    };
  }, [isConnected, localPeer, hmsActions]);

  const handleUserConnectionStatus = () => {
    if (user?.id && socket?.connected) {
      socket.emit("disconnectedUsers", (users) => {
        setDisconnectedUsers(users);
      });

      socket.emit("connectedUsers", (users) => {
        setConnectedUsers(users);
      });
    }
  };

  useEffect(() => {
    // if (socket?.connected && user?.id) {
    //   handleUserConnectionStatus();

    //   const intervalId = setInterval(() => {
    //     handleUserConnectionStatus();
    //   }, 10000); // Update every 10 seconds

    //   return () => {
    //     clearInterval(intervalId);
    //   };
    // }
  }, [socket, user]);

  // Load the workshop once socket is connected
  useEffect(() => {
    if (socket?.connected) {
      setLoad(true);

      if (pdf) return;

      try {
        // Check if userId exists in cookies, if yes, then get user details from socket and set it in context
        const userId = Cookies.get("userId");
        if (userId) {
          socket.emit("getUserData", userId, (user) => {
            if (user) {
              setUser({ ...user, m, p, mp });
              toast.success(user?.name + ", You are connected again.");

              // For online workshops, always send the user through PreReady
              // which requires user interaction before 100ms media starts.
              if (window.location.pathname.includes("/online")) {
                navigate("/online/pre-ready");
                return;
              }

              // For non-online flows, keep existing behaviour.
              socket.emit("syncToWorkshop", "", (url) => {
                // Check if workshop has started or setup is completed to determine default redirect
                const setupCompleted = Cookies.get("setupCompleted") === "true";
                const defaultUrl =
                  setupCompleted || workshopDetails?.workshopStarted ? "/wait" : "/wait";
                const redirectUrl = url || defaultUrl;
                navigate(redirectUrl);
              });
            } else {
              Cookies.remove("userId");
              navigate("/online");
            }
          });
        } else {
          if (!(window.location.pathname !== "/online") && !p && !m && !mp) navigate("/not-found");
          else if (window.location.pathname.includes("online/insights") && !p && !m && !mp)
            console.log("insights");
          else navigate("/online");
        }
      } catch (error) {
        navigate("/not-found");
        console.log(error);
        console.log("Cookies Unavailable");
      }
    }
  }, [socket, workshopDetails?.workshopStarted]);

  useEffect(() => {
    if (socket?.connected && user?.id) {
      // Check if page is unlocked
      if (NonPrivateRoutes.includes(location.pathname)) {
        setLocked(false);
        return;
      }
      socket.emit("lockedPage", location.pathname, (unlocked) => {
        if (
          pdf ||
          location.pathname?.includes("pre-summary") ||
          location.pathname?.includes("insights")
        ) {
          setLocked(false);
          return;
        }

        setLocked(!unlocked);
        if (!unlocked) toast.error("Locked");
      });
    }
  }, [location.pathname, user, socket]);

  useEffect(() => {
    if (!user?.id || !socket) return;

    socket.on("navigate", (path) => {
      console.log("navigate", path);
      if (!user?.id) return;
      navigate(path);
    });
  }, [socket, user]);

  if (!load || !socket) return <Loader message="Setting up the workshop" />;

  if (locked) return <LockedPage />;
  else
    return (
      <>
        <div className={"bg-gray-250 min-h-dvh"}>
          <Outlet />
        </div>

        {(user?.role === defaultRoles.moderator ||
          user?.role === defaultRoles.moderatorAndParticipant) && <NewUserNotification />}
      </>
    );
};

const InsightsContainer = () => {
  const navigate = useNavigate();

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isPdfParam = searchParams.get("pdf");
  const isPdf = isPdfParam === "true";
  const isDemo = searchParams.get("demo") === "true";

  const { socket, setSocket, setThemeConfig, setWorkshopDetails } = useContext(WorkshopContext);

  const [load, setLoad] = useState(false);

  const loadTranslations = async () => {
    try {
      // Fetch translations in parallel
      const [workshopTranslations, resourceTranslations] = await Promise.all([
        fetchTranslations("workshop-insights"),
        fetchTranslations("workshop-resources")
      ]);

      // Create resources object with fallback to English if a language is missing
      const resources = {};
      VALID_LANGUAGES.forEach((lang) => {
        const workshopLangData =
          workshopTranslations.data?.[lang] || workshopTranslations.data?.en || {};
        const resourceLangData =
          resourceTranslations.data?.[lang] || resourceTranslations.data?.en || {};

        resources[lang] = {
          translations: {
            ...workshopLangData,
            resources: resourceLangData
          }
        };
      });

      // Get and validate language parameter
      const langParam = searchParams.get("lang")?.toLowerCase();
      const selectedLanguage = VALID_LANGUAGES.includes(langParam) ? langParam : "en";

      await i18next.use(initReactI18next).init({
        interpolation: { escapeValue: false },
        saveMissing: false,
        returnEmptyString: false,
        fallbackLng: "en",
        lng: selectedLanguage,
        languages: VALID_LANGUAGES,
        ns: ["translations"],
        defaultNS: "translations",
        resources
      });
    } catch (error) {
      console.error("Failed to load translations:", error);
      await i18next.use(initReactI18next).init({
        lng: "en",
        fallbackLng: "en"
      });
    }
  };

  useEffect(() => {
    try {
      document.title = "Mission Team Insights";
    } catch (error) {
      console.log(error);
    }

    (async () => {
      try {
        const {
          data: {
            languageId,
            roomData,
            teamName,
            platform,
            type,
            workshopDate,
            dateTime,
            workshopDateString,
            workshopTimeString,
            mftPublic,
            mftPublicTeam
          } = {},
          mtW
        } = await getWorkshopInsightsData(id);

        await loadTranslations();

        // Connect to socket
        const socketConnection = io(process.env.REACT_APP_SOCKET_URL + "/workshop", {
          extraHeaders: {
            workshopId: id,
            platform: mtW ? (type === "online" ? "mtOnline" : "mt") : "mft"
          }
        });

        socketConnection.on("connect", () => {
          console.log("Insights Socket connected");
          setSocket(socketConnection);
        });

        setThemeConfig({ primary: "#E59536", primary50: hexToRgba("#E59536", 50) });
        setWorkshopDetails({
          id: id,
          roomData: roomData,
          teamName: teamName,
          pdf: isPdf,
          platform: platform,
          workshopDateString: workshopDateString || workshopDate,
          workshopDate: workshopDate,
          dateTime: dateTime,
          workshopTimeString: workshopTimeString || dateTime,
          demoWorkshop: isDemo,
          mftPublic: mftPublic,
          mftPublicTeam: mftPublicTeam
        });
        i18next.changeLanguage(languageId);

        try {
          document.documentElement.setAttribute("lang", languageId);
        } catch (error) {
          console.log("HTML lang attribute not found");
        }
      } catch (error) {
        console.log(error);
        toast.error(error?.response?.data || "Workshop not found");
        navigate(`/not-found`);
      }
    })();
  }, []);

  useEffect(() => {
    if (socket?.connected) {
      setLoad(true);
    }
  }, [socket]);

  if (!load) return <Loader message="Setting up the Insights page" />;
  else
    return (
      <div className={"w-full mx-auto"}>
        <Outlet />
      </div>
    );
};

const BoosterContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  let id = searchParams.get("id"); // workshop unique id

  const [locked, setLocked] = useState(false);

  try {
    // Remove previous user ID as it is a new login to workshop
    if (id) Cookies.remove("userId");
  } catch (error) {
    console.log("Cookies Unavailable");
  }

  if (!id) {
    try {
      id = Cookies.get("bid"); // Try to get id from cookies
    } catch (error) {
      console.log("Cookies Unavailable");
    }
  }

  const { socket, setSocket, setThemeConfig, user, setUser, setWorkshopDetails, setWorkshopUsers } =
    useContext(WorkshopContext);

  const [load, setLoad] = useState(false);

  const loadTranslations = async () => {
    const { data: { en, de, fr, it } = {} } = await fetchTranslations("booster-workshop");
    const { data: { en: en_, de: de_, fr: fr_, it: it_ } = {} } =
      await fetchTranslations("workshop-resources");

    i18next.addResourceBundle("en", "translations", { ...en, resources: { ...en_ } });
    i18next.addResourceBundle("fr", "translations", { ...fr, resources: { ...fr_ } });
    i18next.addResourceBundle("de", "translations", { ...de, resources: { ...de_ } });
    i18next.addResourceBundle("it", "translations", { ...it, resources: { ...it_ } });

    i18next.changeLanguage("en"); // Initial language
  };

  useEffect(() => {
    i18next.init({
      interpolation: { escapeValue: false },
      saveMissing: false,
      returnEmptyString: false
    });
    i18next.languages = ["en", "fr", "de", "it"];
    i18next.use(initReactI18next).init({
      fallbackLng: "en",
      lng: "en",
      ns: ["translations"],
      defaultNS: "translations"
    });

    (async () => {
      try {
        const { data: { language } = {} } = await getBoosterWorkshopDetails(id);

        await loadTranslations();
        setUser({ id: null, name: "" });
        setThemeConfig({ primary: "#E59536", primary50: hexToRgba("#E59536", 50) });
        setWorkshopDetails({ id: id });
        i18next.changeLanguage(language);

        try {
          document.documentElement.setAttribute("lang", language);
        } catch (error) {
          console.log("HTML lang attribute not found");
        }

        try {
          // Store in cookies that expire in 3 hours
          if (id) Cookies.set("bid", id, { expires: 0.125 });
        } catch (error) {
          console.log("Cookies Unavailable");
        }

        // Connect to socket
        const socketConnection = io(process.env.REACT_APP_SOCKET_URL + "/booster", {
          extraHeaders: {
            workshopId: id
          }
        });

        socketConnection.on("connect", () => {
          console.log("Booser Workshop Socket connected");
          setSocket(socketConnection);

          socketConnection.on("users", (users) => {
            setWorkshopUsers(users);
          });

          socketConnection.on("navigate", (path) => {
            navigate(path);
          });

          socketConnection.on("workshopEnded", (status) => {
            if (status) {
              navigate("/booster/end");
              // toast.info("This works is ended");
            }
          });

          socketConnection.on("disconnect", () => {
            setSocket(null);
            navigate("/booster");
            toast.error(
              "You are disconnected from server, please use the login link again to join the workshop."
            );
          });

          socketConnection.on("error", (error) => {
            toast.error(error);
            console.log(error);
          });
        });
      } catch (error) {
        console.log(error);
        toast.error(error?.response?.data || "Workshop not found");
        navigate(`/not-found`);
      }
    })();
  }, []);

  // Load the workshop once socket is connected
  useEffect(() => {
    if (socket?.connected) {
      setLoad(true);

      try {
        // Check if userId exists in cookies, if yes, then get user details from socket and set it in context
        const userId = Cookies.get("userId");
        if (userId) {
          socket.emit("getUserData", userId, (user) => {
            console.log("user", user, "userId", userId);
            if (user) {
              setUser({ ...user, id: userId });
              if (window.location.pathname === "/" || window.location.pathname === "/booster") {
                navigate("/booster/wait");
                return;
              }
            } else {
              Cookies.remove("userId");
              navigate("/booster");
            }
          });
        } else {
          console.log(!(window.location.pathname !== "/booster") && !id);
          if (!(window.location.pathname !== "/booster") && !id) {
            navigate("/not-found");
          } else {
            if (window.location.pathname === "/booster") navigate("/booster");
            else navigate("/booster");
          }
        }
      } catch (error) {
        navigate("/not-found");
        console.log(error);
        console.log("Cookies Unavailable");
      }
    }
  }, [socket]);

  useEffect(() => {
    if (socket?.connected && user?.id) {
      // Check if page is unlocked
      if (NonPrivateRoutes.includes(location.pathname)) {
        setLocked(false);
        return;
      }
      socket.emit("lockedPage", location.pathname, (unlocked) => {
        setLocked(!unlocked);
        if (!unlocked) toast.error("Locked");
      });
    }
  }, [location.pathname, user, socket]);

  if (!load || !socket) return <Loader message="Setting up the booster workshop" />;

  if (locked) return <LockedPage />;
  else
    return (
      <div className={"max-w-[1000px] mx-auto"}>
        <Outlet />
      </div>
    );
};

const NotFound = () => {
  return (
    <div className="z-50 backdrop-blur-sm fixed top-0 left-0 w-full h-full flex items-center justify-center">
      <p className="text-black text-2xl">Not found</p>
    </div>
  );
};

const LockedPage = () => {
  return (
    <div className="z-50 backdrop-blur-sm fixed top-0 left-0 w-full h-full flex items-center justify-center">
      <p className="text-black text-2xl">Locked</p>
    </div>
  );
};

const OnlineDemoContainer = () => {
  const {
    setSocket,
    setThemeConfig,
    setUser,
    setWorkshopDetails,
    setPlatform,
    setWorkshopUsers,
    setConnectedUsers
  } = useContext(WorkshopContext);

  const [load, setLoad] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const handleSocketEmit = (event, data) => {
    if (event === "startWorkshop") {
      navigate("/online-demo/video/1");
    } else if (event === "navigate") {
      navigate(data);
    }
  };

  // Create a mock socket that does nothing
  const mockSocket = {
    connected: true,
    emit: handleSocketEmit,
    on: () => {},
    off: () => {},
    disconnect: () => {},
    connect: () => {}
  };

  const loadTranslations = async () => {
    try {
      // Fetch translations in parallel
      const [workshopTranslations, resourceTranslations] = await Promise.all([
        fetchTranslations("demo-mobile-workshop"),
        fetchTranslations("workshop-resources")
      ]);

      // Create resources object with fallback to English if a language is missing
      const resources = {};
      VALID_LANGUAGES.forEach((lang) => {
        const workshopLangData =
          workshopTranslations.data?.[lang] || workshopTranslations.data?.en || {};
        const resourceLangData =
          resourceTranslations.data?.[lang] || resourceTranslations.data?.en || {};

        resources[lang] = {
          translations: {
            ...workshopLangData,
            resources: resourceLangData
          }
        };
      });

      // Get and validate language parameter
      const langParam = searchParams.get("lang")?.toLowerCase();
      const selectedLanguage = VALID_LANGUAGES.includes(langParam) ? langParam : "en";

      await i18next.use(initReactI18next).init({
        interpolation: { escapeValue: false },
        saveMissing: false,
        returnEmptyString: false,
        fallbackLng: "en",
        lng: selectedLanguage,
        languages: VALID_LANGUAGES,
        ns: ["translations"],
        defaultNS: "translations",
        resources
      });
    } catch (error) {
      console.error("Failed to load translations:", error);
      await i18next.use(initReactI18next).init({
        lng: "en",
        fallbackLng: "en"
      });
    }
  };

  const setupWorkshop = async () => {
    const workshopUsers = [];
    let demoUser = getLocalStorage("demoUser");

    // Create URLSearchParams object for query parameters
    const queryParams = new URLSearchParams();

    // Add parameters if they exist
    const langParam = searchParams.get("lang");
    const nameParam = searchParams.get("name");
    const emailParam = searchParams.get("email");

    if ((!nameParam || !emailParam) && !demoUser) {
      navigate("/not-found");
      return;
    }

    if (langParam) queryParams.set("lang", langParam);
    if (nameParam) queryParams.set("name", nameParam);

    // Convert to query string (includes ? prefix if params exist)
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

    // Check if demoUser has all required properties
    const isValidDemoUser =
      demoUser && "id" in demoUser && "name" in demoUser && "role" in demoUser;

    // Create new demo user if invalid, missing properties, or name is different
    if (nameParam && (!isValidDemoUser || demoUser.name !== nameParam)) {
      demoUser = {
        id: "demo",
        name: nameParam,
        role: defaultRoles.moderatorAndParticipant
      };
      setLocalStorage("demoUser", demoUser);
    }

    if (demoUser) workshopUsers.push(demoUser);
    else if (!["/online-demo", "/online-demo/wait"].includes(window.location.pathname)) {
      navigate(`/online-demo${queryString}`);
      clearLocalStorage();
    }

    workshopUsers.push(...demoWorkshopUsers());
    await loadTranslations();
    setPlatform(defaultPlatforms.mtOnlineDemo);
    setUser(demoUser || {});
    setWorkshopDetails({
      platform: defaultPlatforms.mtOnlineDemo,
      teamName: "Team Explorers",
      users: workshopUsers.length
    });
    setWorkshopUsers(workshopUsers);
    setThemeConfig({ primary: "#E59536", primary50: hexToRgba("#E59536", 50) });
    setSocket(mockSocket); // Set the mock socket instead of null
    setConnectedUsers(workshopUsers);
    setLoad(true);
  };

  useEffect(() => {
    setupWorkshop();
  }, []);

  useEffect(() => {
    if (load) {
      if (
        window.location.pathname === "/online-demo" ||
        window.location.pathname === "/online-demo/"
      ) {
        clearLocalStorage();
        setupWorkshop();
      }
    }
  }, [window.location.pathname, load]);

  if (!load) return <Loader message="Setting up the online demo workshop" />;

  return (
    <div className={"bg-gray-250 min-h-dvh"}>
      <Outlet />
    </div>
  );
};

export default App;
